(() => {
  const RESUME_DELAY = 2500;
  const MOBILE_ADVANCE_DELAY = 3200;
  const MOBILE_SETTLE_DELAY = 520;
  const DRAG_THRESHOLD = 5;

  const initEvidenceRails = (root = document) => {
    root.querySelectorAll('[data-evidence-rail]:not([data-evidence-ready])').forEach((section) => {
      const viewport = section.querySelector('[data-evidence-viewport]');
      const track = section.querySelector('[data-evidence-track]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobileCarousel = window.matchMedia('(max-width: 749px)');

      if (!viewport || !track) return;

      section.dataset.evidenceReady = 'true';

      let animationFrame = null;
      let lastFrameTime = null;
      let mobileAdvanceTimer = null;
      let resumeTimer = null;
      let isPaused = false;
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let movedDistance = 0;
      let suppressClick = false;
      let position = viewport.scrollLeft;
      const speed = Number(section.dataset.autoScrollSpeed) || 24;
      const firstCard = track.querySelector('.krasis-evidence-card');
      const loopStart = track.querySelector('[data-evidence-loop-start]');

      const getLoopWidth = () => {
        if (!firstCard || !loopStart) return 0;
        return loopStart.offsetLeft - firstCard.offsetLeft;
      };

      const getCardStep = () => {
        if (!firstCard) return 0;
        const trackStyles = window.getComputedStyle(track);
        const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
        return firstCard.offsetWidth + gap;
      };

      const normalizePosition = () => {
        const loopWidth = getLoopWidth();
        if (!loopWidth) return;

        if (position >= loopWidth) {
          position %= loopWidth;
        } else if (position < 0) {
          position += loopWidth * Math.ceil(Math.abs(position) / loopWidth);
        }

        viewport.scrollLeft = position;
      };

      const syncPosition = () => {
        position = viewport.scrollLeft;
        normalizePosition();
      };

      const stopAnimation = () => {
        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }
        lastFrameTime = null;
      };

      const stopMobileAdvance = () => {
        window.clearTimeout(mobileAdvanceTimer);
        mobileAdvanceTimer = null;
      };

      const tick = (timestamp) => {
        if (isPaused || reducedMotion.matches || document.hidden) {
          stopAnimation();
          return;
        }

        if (lastFrameTime == null) {
          lastFrameTime = timestamp;
        }

        const elapsed = Math.min(timestamp - lastFrameTime, 50);
        position += (speed * elapsed) / 1000;
        normalizePosition();
        lastFrameTime = timestamp;
        animationFrame = window.requestAnimationFrame(tick);
      };

      const startAnimation = () => {
        if (
          animationFrame
          || isPaused
          || reducedMotion.matches
          || mobileCarousel.matches
          || document.hidden
          || !getLoopWidth()
        ) return;
        lastFrameTime = null;
        animationFrame = window.requestAnimationFrame(tick);
      };

      const startMobileAdvance = () => {
        if (
          mobileAdvanceTimer
          || isPaused
          || reducedMotion.matches
          || !mobileCarousel.matches
          || document.hidden
          || !getLoopWidth()
        ) return;

        mobileAdvanceTimer = window.setTimeout(() => {
          mobileAdvanceTimer = null;

          const step = getCardStep();
          const loopWidth = getLoopWidth();
          if (!step || !loopWidth) return;

          const currentIndex = Math.round(viewport.scrollLeft / step);
          const targetPosition = Math.min((currentIndex + 1) * step, loopWidth);

          position = targetPosition;
          viewport.scrollTo({
            left: targetPosition,
            behavior: 'smooth',
          });

          mobileAdvanceTimer = window.setTimeout(() => {
            mobileAdvanceTimer = null;

            if (targetPosition >= loopWidth) {
              position = 0;
              viewport.scrollLeft = 0;
            } else {
              position = viewport.scrollLeft;
            }

            startMobileAdvance();
          }, MOBILE_SETTLE_DELAY);
        }, MOBILE_ADVANCE_DELAY);
      };

      const pauseForInteraction = () => {
        isPaused = true;
        window.clearTimeout(resumeTimer);
        stopAnimation();
        stopMobileAdvance();
      };

      const scheduleResume = () => {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(() => {
          isPaused = false;
          startAnimation();
          startMobileAdvance();
        }, RESUME_DELAY);
      };

      const endDrag = (event) => {
        if (mobileCarousel.matches) {
          syncPosition();
          scheduleResume();
          return;
        }

        if (!isDragging) return;

        isDragging = false;
        viewport.classList.remove('is-dragging');

        if (event.pointerId && viewport.hasPointerCapture(event.pointerId)) {
          viewport.releasePointerCapture(event.pointerId);
        }

        if (movedDistance > DRAG_THRESHOLD) {
          suppressClick = true;
          window.setTimeout(() => {
            suppressClick = false;
          }, 0);
        }

        syncPosition();
        scheduleResume();
      };

      viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        if (mobileCarousel.matches) {
          pauseForInteraction();
          return;
        }

        isDragging = true;
        startX = event.clientX;
        startScrollLeft = viewport.scrollLeft;
        movedDistance = 0;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture(event.pointerId);
        pauseForInteraction();
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!isDragging) return;

        const deltaX = event.clientX - startX;
        movedDistance = Math.max(movedDistance, Math.abs(deltaX));
        viewport.scrollLeft = startScrollLeft - deltaX;
        syncPosition();
      });

      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
      viewport.addEventListener('lostpointercapture', endDrag);

      viewport.addEventListener(
        'wheel',
        (event) => {
          const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? event.deltaX
            : event.shiftKey
              ? event.deltaY
              : 0;

          if (Math.abs(horizontalDelta) < 1) return;

          pauseForInteraction();
          event.preventDefault();
          viewport.scrollLeft += horizontalDelta;
          syncPosition();
          scheduleResume();
        },
        { passive: false }
      );

      viewport.addEventListener(
        'click',
        (event) => {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      viewport.addEventListener(
        'scroll',
        () => {
          if (mobileCarousel.matches) position = viewport.scrollLeft;
        },
        { passive: true }
      );

      viewport.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        pauseForInteraction();

        if (event.key === 'ArrowLeft') viewport.scrollLeft -= viewport.clientWidth * 0.72;
        if (event.key === 'ArrowRight') viewport.scrollLeft += viewport.clientWidth * 0.72;
        if (event.key === 'Home') viewport.scrollLeft = 0;
        if (event.key === 'End') viewport.scrollLeft = getLoopWidth();

        syncPosition();
        scheduleResume();
      });

      const handleMotionPreference = () => {
        if (reducedMotion.matches) {
          stopAnimation();
          stopMobileAdvance();
          return;
        }

        startAnimation();
        startMobileAdvance();
      };

      const handleCarouselModeChange = () => {
        stopAnimation();
        stopMobileAdvance();
        syncPosition();
        startAnimation();
        startMobileAdvance();
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          stopAnimation();
          stopMobileAdvance();
          return;
        }

        startAnimation();
        startMobileAdvance();
      };

      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', handleMotionPreference);
      } else if (typeof reducedMotion.addListener === 'function') {
        reducedMotion.addListener(handleMotionPreference);
      }

      if (typeof mobileCarousel.addEventListener === 'function') {
        mobileCarousel.addEventListener('change', handleCarouselModeChange);
      } else if (typeof mobileCarousel.addListener === 'function') {
        mobileCarousel.addListener(handleCarouselModeChange);
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);

      const resizeObserver = new ResizeObserver(() => {
        if (!getLoopWidth()) {
          stopAnimation();
          stopMobileAdvance();
          return;
        }

        syncPosition();
        startAnimation();
        startMobileAdvance();
      });

      resizeObserver.observe(track);
      if (firstCard) resizeObserver.observe(firstCard);
      if (loopStart) resizeObserver.observe(loopStart);

      window.requestAnimationFrame(() => {
        if (!getLoopWidth()) return;
        position = viewport.scrollLeft;
        startAnimation();
        startMobileAdvance();
      });
    });
  };

  initEvidenceRails();
  document.addEventListener('shopify:section:load', (event) => initEvidenceRails(event.target));
})();
